// CoalitionPerformanceBox.jsx
import { Box, Typography, Grid } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  Cell, PieChart, Pie ,Label
} from "recharts";
import { scaleLinear } from "d3-scale"; 
import { interpolateHcl } from "d3-interpolate";

const COALITION_PARTIES = {
  rightcoalition:"Right Coalition", 
  nat:"National", 
  act:"ACT", 
  nzf:"NZF",
  leftcoalition:"Left Coalition", 
  lab:"Labour", 
  green:"Green", 
  tpm:"TPM"
}
const COALITION_LABELS = {
  rightcoalition:"coalition", 
  leftcoalition:"coalition", 
  nat:"nat_pred_n", 
  act:"act_pred_n", 
  nzf:"nzf_pred_n", 
  lab:"lab_pred_n", 
  green:"green_pred_n", 
  tpm:"tpm_pred_n"
}
const COLORS = {
  rightcoalition: ["#111184", "#e0e0e0"],
  leftcoalition: ["#8B0000", "#e0e0e0"],
  nat: ["#0052cc", "#e0e0e0"],
  act: ["#ffd700", "#e0e0e0"],
  nzf: ["#000000", "#e0e0e0"],
  lab: ["#E82C2C", "#e0e0e0"],
  green: ["#009639", "#e0e0e0"],
  tpm: ["#CC0033", "#e0e0e0"],
};



export default function CoalitionPerformanceBox({ metricRowsConst, metricRowsNat,selectedFeature }) {
    if(!metricRowsNat){return}

    let row
  if(selectedFeature && metricRowsConst){
    row = metricRowsConst.filter(
                                (g) => Number(g.area_id) === Number(selectedFeature.area_id)
                                )[0]
  }else{  
  row = metricRowsNat?.[0] || {};
  
}
const parties = Object.keys(COALITION_PARTIES);

  const ringdata = parties.map((party) =>
    
    
    {
      let currentShare 
      if(party==="rightcoalition"){
        currentShare = Number(row?.["nat_pred_n"])+Number(row?.["act_pred_n"])+Number(row?.["nzf_pred_n"])
      }else if(party==="leftcoalition"){
        currentShare = Number(row?.["lab_pred_n"])+Number(row?.["green_pred_n"])+Number(row?.["tpm_pred_n"])
      }else{
        currentShare = Number(row?.[COALITION_LABELS[party]])
      }
      let considerShare
      if(party==="rightcoalition"){
        considerShare = Number(row?.["coalition_consider"])/10
      } else if(party==="leftcoalition"){
        considerShare = Number(row?.["coalition_left_consider"])/10
      }else{
        considerShare = Number(row?.[`${party}_consider`])/10;
      }
      return {party:party,considerShare:considerShare,currentShare:currentShare}
    
  })



  // Net "good job" score
  const netGoodJob = (row.gov_good_n || 0) - (row.gov_bad_n || 0);

  // Issue data (order them however you want to show them)
let issueData = [
  { key: "econ_issue_n", label: "Economy" },
  { key: "health_issue_n", label: "Health" },
  { key: "education_issue_issue_n", label: "Education" },
  { key: "crime_issue_n", label: "Crime" },
  { key: "housing_issue_n", label: "Housing" },
  { key: "inflation_issue_n", label: "Inflation" },
  { key: "climate_issue_n", label: "Climate" },
  { key: "poverty_issue_n", label: "Poverty" },
  { key: "immigration_issue_n", label: "Immigration" },
  { key: "transport_issue_n", label: "Transport" },
  { key: "defence_issue_n", label: "Defence" },
  { key: "drugs_issue_n", label: "Drugs" },
  { key: "maori_issue_n", label: "Māori Issues" },
  { key: "pollution_issue_n", label: "Pollution" },
  { key: "race_rel_issue_n", label: "Race Relations" },
  { key: "unemployment_issue_n", label: "Unemployment" },
  { key: "tax_issue_n", label: "Tax" },
].map(({ key, label }) => ({
  issue: label,
  value: row[key] || 0,
}));

// Sort by descending percentage
issueData.sort((a, b) => b.value - a.value);

const vals = issueData.map(d => d.value);
const minVal = Math.min(...vals);
const maxVal = Math.max(...vals);
const midVal = (minVal + maxVal) / 2;

// green (low) -> yellow (mid) -> red (high), using HCL to avoid brown
const colorScale = scaleLinear()
  .domain(
    minVal === maxVal
      ? [minVal - 1, minVal, minVal + 1] // edge case: flat data
      : [minVal, midVal, maxVal]
  )
  .range(["#D32F2F", "#FBC02D","#2E7D32"])
  .interpolate(interpolateHcl)
  .clamp(true);
  return (
    
    <Box sx={{ p: 2, border: "1px solid #ddd", borderRadius: 2}}>
      {/* Title */}
      <Typography variant="h6" gutterBottom>
        Coalition Performance
      </Typography>

      {/* Net score + bar chart */}
      <Box sx={{ display: "flex", alignItems: "center", mb:1, ml:2 }}>
        <Typography
          variant="h3"
          sx={{ fontWeight: "bold", mr: 2 }}
          color={netGoodJob >= 0 ? "success.main" : "error.main"}
        >
          {Math.round(netGoodJob)}%
        </Typography>
        <Typography variant="body2" sx={{ maxWidth: 160, mr: 1}}>
          think the Government is doing a net ‘good job’
        </Typography>

        <Box sx={{ flex: 1, height: 100 }}>
          <ResponsiveContainer width="100%" height={100}>
            <BarChart data={issueData}
  margin={{ left: 40, bottom:5 }}  >
              <XAxis 
                dataKey="issue" 
                interval={0} 
                angle={-35} 
                textAnchor="end" 
                height={45} 
                tick={{ fontSize: 9, fill: "#333" }} 
              />
              <Tooltip
                cursor={{ fill: "rgba(0,0,0,0.05)" }}
                formatter={(val, _, obj) => [`${val.toFixed(1)}%`, obj.issue]}
              />

              <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                {issueData.map((d, i) => (
                  <Cell key={i} fill={colorScale(d.value)} />
                ))}

              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {/* Description */}
      <Typography variant="body2" paragraph>
        The pie charts below show how well the Coalition is currently
        performing as well as how well they could perform – the bolded
        number (and outer circle) indicates each group’s current vote
        share, while the second percentage (and inner circle) shows the
        percentage who would consider voting for them.
      </Typography>

      {/* Placeholder for pie charts */}
       <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                gap:0.5,
                justifyItems: "center",
              }}
            >
              {ringdata.map(({ party, currentShare, considerShare }) => {
                const displayParty =
                  party === "nat"
                    ? "National"
                    : party === "act"
                    ? "ACT"
                    : party === "nzf"
                    ? "NZF"
                    : party === "rightcoalition"
                    ? "Right"
                    : party === "leftcoalition"
                    ? "Left"
                    : party === "lab"
                    ? "Labour"
                    : party === "green"
                    ? "Green"
                    :  party === "tpm"
                    ? "TPM"
                    :"";

                const outerData = [
                  { value: currentShare || 0 },
                  { value: 100 - (currentShare || 0) },
                ];
                const innerData = [
                  { value: considerShare || 0 },
                  { value: 100 - (considerShare || 0) },
                ];

                return (
                  <Grid item xs={6} md={3} key={party}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <PieChart width={140} height={140}>
                        <Pie
                          data={outerData}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={60}
                          startAngle={90}
                          endAngle={-270}
                        >
                          <Cell fill={COLORS[party][0]} />
                          <Cell fill={COLORS[party][1]} />
                        </Pie>

                        <Pie
                          data={innerData}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={40}
                          startAngle={90}
                          endAngle={-270}
                          labelLine={false}
                        >
                          <Cell fill={COLORS[party][0]} />
                          <Cell fill={COLORS[party][1]} />
                          <Label
                            value={displayParty}
                            position="center"
                            style={{ fontSize: 12,  fontWeight: "bold",textAnchor: "middle", fill: "#333" }}
                          />
                        </Pie>
                      </PieChart>
                      <Typography variant="body2" align="center" sx={{ fontSize: 12 }}>
                        <b>
                          Current:{" "}
                          {isNaN(currentShare) ? "–" : `${currentShare.toFixed(0)}%`}
                        </b>
                      </Typography>
                      <Typography variant="body2" align="center" sx={{ fontSize: 12 }}>
                        Consider:{" "}
                        {isNaN(considerShare) ? "–" : `${considerShare.toFixed(0)}%`}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Box>
    </Box>
  );
}
